import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { searchFranchisees } from '../services/franchiseeService';
import { transferClients, previewClientTransfer } from '../services/utilityService';
import { debounce } from 'lodash';
import { FaExchangeAlt, FaSearch, FaTimes, FaExclamationTriangle, FaArrowRight } from 'react-icons/fa';
import ConfirmationModal from '../components/ConfirmationModal';

const useSearch = (searchFunction) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const debouncedSearch = useCallback(
        debounce(async (term) => {
            if (term.trim().length < 3) {
                setSuggestions([]);
                setIsSearching(false);
                setShowSuggestions(false);
                return;
            }

            setIsSearching(true);
            try {
                const data = await searchFunction({ searchString: term, pageSize: 10 });
                setSuggestions(data.franchisees || []);
                setShowSuggestions(true);
            } catch (error) {
                console.error('Search error:', error);
                setSuggestions([]);
                setShowSuggestions(false);
            } finally {
                setIsSearching(false);
            }
        }, 300),
        [searchFunction]
    );

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        if (value.trim().length >= 3) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
        debouncedSearch(value);
    };

    const clearSearch = () => {
        setSearchTerm('');
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const hideSuggestions = () => {
        setShowSuggestions(false);
    };

    return {
        searchTerm,
        suggestions,
        isSearching,
        showSuggestions,
        handleSearchChange,
        clearSearch,
        setShowSuggestions,
        hideSuggestions
    };
};

const MassClientTransferUtility = ({ onBack }) => {
    // State for selected franchisees
    const [sourceFranchisee, setSourceFranchisee] = useState(null);
    const [targetFranchisee, setTargetFranchisee] = useState(null);

    // State for preview
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [previewError, setPreviewError] = useState(null);

    // State for transfer operation
    const [isTransferring, setIsTransferring] = useState(false);
    const [transferResult, setTransferResult] = useState(null);
    const [transferError, setTransferError] = useState(null);

    // State for confirmation modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // State for success/error messages
    const [successMessage, setSuccessMessage] = useState(null);
    const [apiError, setApiError] = useState(null);

    // State for household selection (in preview)
    const [selectedHouseholdIds, setSelectedHouseholdIds] = useState(new Set());

    // Search hooks for source and target franchisees
    const sourceSearch = useSearch(searchFranchisees);
    const targetSearch = useSearch(searchFranchisees);

    const handleSourceSelect = (franchisee) => {
        setSourceFranchisee(franchisee);
        setPreviewData(null);
        setPreviewError(null);
        setSelectedHouseholdIds(new Set());
        sourceSearch.clearSearch();
        sourceSearch.hideSuggestions();
    };

    const handleTargetSelect = (franchisee) => {
        if (sourceFranchisee && franchisee.id === sourceFranchisee.id) {
            return;
        }
        setTargetFranchisee(franchisee);
        setPreviewData(null);
        setPreviewError(null);
        setSelectedHouseholdIds(new Set());
        targetSearch.clearSearch();
        targetSearch.hideSuggestions();
    };

    const clearSourceSelection = () => {
        setSourceFranchisee(null);
        setPreviewData(null);
        setPreviewError(null);
        setSelectedHouseholdIds(new Set());
        sourceSearch.clearSearch();
    };

    const clearTargetSelection = () => {
        setTargetFranchisee(null);
        setPreviewData(null);
        setPreviewError(null);
        setSelectedHouseholdIds(new Set());
        targetSearch.clearSearch();
    };

    // Auto-trigger preview whenever both franchisees are selected
    useEffect(() => {
        if (!sourceFranchisee || !targetFranchisee || sourceFranchisee.id === targetFranchisee.id) {
            return;
        }
        let cancelled = false;
        const fetchPreview = async () => {
            setIsPreviewing(true);
            setPreviewError(null);
            setPreviewData(null);
            setApiError(null);
            setSelectedHouseholdIds(new Set());
            try {
                const result = await previewClientTransfer(sourceFranchisee.id, targetFranchisee.id);
                if (!cancelled) {
                    setPreviewData(result.data);
                }
            } catch (error) {
                if (!cancelled) {
                    setPreviewError(error.message);
                    setApiError(error.message);
                }
            } finally {
                if (!cancelled) setIsPreviewing(false);
            }
        };
        fetchPreview();
        return () => { cancelled = true; };
    }, [sourceFranchisee?.id, targetFranchisee?.id]);

    const handleTransferClick = () => {
        if (!sourceFranchisee || !targetFranchisee) {
            return;
        }
        setShowConfirmModal(true);
    };

    const handleConfirmTransfer = async () => {
        setShowConfirmModal(false);
        setIsTransferring(true);
        setTransferError(null);
        setTransferResult(null);
        setApiError(null);

        try {
            console.log('[INFO] Starting client transfer...');
            const result = await transferClients(
                sourceFranchisee.id,
                targetFranchisee.id,
                previewData ? Array.from(selectedHouseholdIds) : null
            );
            console.log('[INFO] Transfer completed successfully:', result);
            setTransferResult(result);
            setSuccessMessage(result.message);
        } catch (error) {
            console.error('[ERROR] Transfer failed:', error);
            setTransferError(error.message);
            setApiError(error.message);
        } finally {
            setIsTransferring(false);
        }
    };

    const resetTransfer = () => {
        setSourceFranchisee(null);
        setTargetFranchisee(null);
        setPreviewData(null);
        setPreviewError(null);
        setTransferResult(null);
        setTransferError(null);
        setSuccessMessage(null);
        setApiError(null);
        setSelectedHouseholdIds(new Set());
        sourceSearch.clearSearch();
        targetSearch.clearSearch();
    };

    const clearPreview = () => {
        setPreviewData(null);
        setPreviewError(null);
        setSelectedHouseholdIds(new Set());
    };

    const allHouseholdIds = previewData?.householdDetails?.map(h => h.id) || [];
    const isAllSelected = allHouseholdIds.length > 0 && allHouseholdIds.every(id => selectedHouseholdIds.has(id));
    const isSomeSelected = allHouseholdIds.some(id => selectedHouseholdIds.has(id)) && !isAllSelected;
    const selectedHouseholds = previewData?.householdDetails?.filter(h => selectedHouseholdIds.has(h.id)) || [];
    const selectedSchedulesCount = selectedHouseholds.reduce((sum, h) => sum + (h.scheduleCount || 0), 0);
    const selectedPendingJobsCount = selectedHouseholds.reduce((sum, h) => sum + (h.pendingJobCount || 0), 0);

    const handleToggleHousehold = (id) => {
        setSelectedHouseholdIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedHouseholdIds(new Set());
        } else {
            setSelectedHouseholdIds(new Set(allHouseholdIds));
        }
    };

    const canTransfer = sourceFranchisee && targetFranchisee &&
                       sourceFranchisee.id !== targetFranchisee.id &&
                       !isTransferring && !isPreviewing &&
                       (!previewData || selectedHouseholdIds.size > 0);

    return (
        <>
            <div className="container mx-auto px-4 py-8">
            {/* Page Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-jims-cyan-dark">Mass Client Transfer</h1>
                    <p className="text-gray-600 mt-1">Transfer all clients and their pending jobs from one franchisee to another</p>
                </div>
                <button
                    onClick={onBack}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                    ← Back to Utilities
                </button>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
                    {successMessage}
                </div>
            )}

            {/* Error Message */}
            {apiError && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
                    Error: {apiError}
                </div>
            )}

            {/* Mass Client Transfer Card */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <FaExchangeAlt className="h-6 w-6 text-jims-cyan-dark" />
                        </div>
                        <div className="ml-3">
                            <h2 className="text-lg font-semibold text-gray-900">Mass Client Transfer</h2>
                            <p className="text-sm text-gray-600">
                                Transfer all clients and their pending jobs from one franchisee to another
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {/* Transfer Form */}
                    <div className="flex gap-8 items-center justify-center">
                        {/* Source Franchisee Selection */}
                        <div className="flex-1">
                            <label className="block text-sm font-semibold text-gray-900 mb-3">
                                Source Franchisee
                                <span className="text-xs font-normal text-gray-500 ml-2">(Transfer FROM)</span>
                            </label>
                            {sourceFranchisee ? (
                                <div className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0">
                                            <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-medium text-gray-600">
                                                    {sourceFranchisee.franchiseName?.charAt(0) || 'F'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="ml-3">
                                            <div className="font-medium text-gray-900">{sourceFranchisee.franchiseName}</div>
                                            <div className="text-sm text-gray-600">
                                                {sourceFranchisee.fseCode} • {sourceFranchisee.firstName} {sourceFranchisee.lastName}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={clearSourceSelection}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                        title="Remove selection"
                                    >
                                        <FaTimes className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search by name or code..."
                                            value={sourceSearch.searchTerm}
                                            onChange={(e) => sourceSearch.handleSearchChange(e.target.value)}
                                            onFocus={() => sourceSearch.setShowSuggestions(true)}
                                            onBlur={() => {
                                                // Delay hiding to allow click on suggestions
                                                setTimeout(() => sourceSearch.hideSuggestions(), 150);
                                            }}
                                            className="border border-gray-300 rounded-md py-2 px-4 pl-10 w-full focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:border-transparent"
                                        />
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        {sourceSearch.searchTerm && (
                                            <button
                                                onClick={sourceSearch.clearSearch}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                                aria-label="Clear search"
                                            >
                                                <FaTimes />
                                            </button>
                                        )}
                                    </div>

                                    {/* Search Results Popover */}
                                    {sourceSearch.showSuggestions && (
                                        <div onMouseDown={(e) => e.preventDefault()} className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                            {sourceSearch.isSearching && (
                                                <div className="px-4 py-3 text-sm text-gray-500">
                                                    <div className="flex items-center">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jims-cyan mr-2"></div>
                                                        Searching...
                                                    </div>
                                                </div>
                                            )}

                                            {!sourceSearch.isSearching && sourceSearch.searchTerm && sourceSearch.searchTerm.length < 3 && (
                                                <div className="px-4 py-3 text-sm text-gray-500">
                                                    Please enter at least 3 characters to search.
                                                </div>
                                            )}

                                            {!sourceSearch.isSearching && sourceSearch.searchTerm && sourceSearch.searchTerm.length >= 3 && sourceSearch.suggestions.length === 0 && (
                                                <div className="px-4 py-3 text-sm text-gray-500">
                                                    No franchisees found matching your search.
                                                </div>
                                            )}

                                            {!sourceSearch.isSearching && sourceSearch.suggestions.length > 0 && (
                                                <>
                                                    {sourceSearch.suggestions.map((franchisee) => (
                                                        <button
                                                            key={franchisee.id}
                                                            onClick={() => handleSourceSelect(franchisee)}
                                                            className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:bg-gray-50 focus:outline-none"
                                                        >
                                                            <div className="font-medium text-gray-900">{franchisee.franchiseName}</div>
                                                            <div className="text-sm text-gray-600">
                                                                {franchisee.fseCode} - {franchisee.firstName} {franchisee.lastName}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Target Franchisee Selection */}
                        <div className="flex-1">
                            <label className="block text-sm font-semibold text-gray-900 mb-3">
                                Target Franchisee
                                <span className="text-xs font-normal text-gray-500 ml-2">(Transfer TO)</span>
                            </label>
                            {targetFranchisee ? (
                                <div className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded-lg">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0">
                                            <div className="h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center">
                                                <span className="text-sm font-medium text-white">
                                                    {targetFranchisee.franchiseName?.charAt(0) || 'F'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="ml-3">
                                            <div className="font-medium text-gray-900">{targetFranchisee.franchiseName}</div>
                                            <div className="text-sm text-gray-600">
                                                {targetFranchisee.fseCode} • {targetFranchisee.firstName} {targetFranchisee.lastName}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={clearTargetSelection}
                                        className="text-gray-400 hover:text-gray-600 transition-colors"
                                        title="Remove selection"
                                    >
                                        <FaTimes className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search by name or code..."
                                            value={targetSearch.searchTerm}
                                            onChange={(e) => targetSearch.handleSearchChange(e.target.value)}
                                            onFocus={() => targetSearch.setShowSuggestions(true)}
                                            onBlur={() => {
                                                // Delay hiding to allow click on suggestions
                                                setTimeout(() => targetSearch.hideSuggestions(), 150);
                                            }}
                                            className="border border-gray-300 rounded-md py-2 px-4 pl-10 w-full focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:border-transparent"
                                        />
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        {targetSearch.searchTerm && (
                                            <button
                                                onClick={targetSearch.clearSearch}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                                aria-label="Clear search"
                                            >
                                                <FaTimes />
                                            </button>
                                        )}
                                    </div>

                                    {/* Search Results Popover */}
                                    {targetSearch.showSuggestions && (
                                        <div onMouseDown={(e) => e.preventDefault()} className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                            {targetSearch.isSearching && (
                                                <div className="px-4 py-3 text-sm text-gray-500">
                                                    <div className="flex items-center">
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-jims-cyan mr-2"></div>
                                                        Searching...
                                                    </div>
                                                </div>
                                            )}

                                            {!targetSearch.isSearching && targetSearch.searchTerm && targetSearch.searchTerm.length < 3 && (
                                                <div className="px-4 py-3 text-sm text-gray-500">
                                                    Please enter at least 3 characters to search.
                                                </div>
                                            )}

                                            {!targetSearch.isSearching && targetSearch.searchTerm && targetSearch.searchTerm.length >= 3 && targetSearch.suggestions.length === 0 && (
                                                <div className="px-4 py-3 text-sm text-gray-500">
                                                    No franchisees found matching your search.
                                                </div>
                                            )}

                                            {!targetSearch.isSearching && targetSearch.suggestions.length > 0 && (
                                                <>
                                                    {targetSearch.suggestions.map((franchisee) => (
                                                        <button
                                                            key={franchisee.id}
                                                            onClick={() => handleTargetSelect(franchisee)}
                                                            className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:bg-gray-50 focus:outline-none ${
                                                                sourceFranchisee && franchisee.id === sourceFranchisee.id ? 'opacity-50 cursor-not-allowed' : ''
                                                            }`}
                                                            disabled={sourceFranchisee && franchisee.id === sourceFranchisee.id}
                                                        >
                                                            <div className={`font-medium ${sourceFranchisee && franchisee.id === sourceFranchisee.id ? 'text-gray-400' : 'text-gray-900'}`}>
                                                                {franchisee.franchiseName}
                                                                {sourceFranchisee && franchisee.id === sourceFranchisee.id && ' (Same as source)'}
                                                            </div>
                                                            <div className={`text-sm ${sourceFranchisee && franchisee.id === sourceFranchisee.id ? 'text-gray-400' : 'text-gray-600'}`}>
                                                                {franchisee.fseCode} - {franchisee.firstName} {franchisee.lastName}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                        {/* Action Buttons */}
                        <div className="pt-8 ">
                            <div className="flex flex-col sm:flex-row gap-3 justify-end">
                                <button
                                    onClick={handleTransferClick}
                                    disabled={!canTransfer}
                                    className={`inline-flex items-center justify-center px-6 py-2 text-sm font-medium rounded-md border transition-all duration-200 ${
                                        canTransfer
                                            ? 'border-jims-cyan text-white bg-jims-cyan hover:bg-jims-cyan-dark'
                                            : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                                    }`}
                                >
                                    {isTransferring ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <FaExchangeAlt className="-ml-1 mr-2 h-4 w-4" />
                                            Execute Transfer
                                        </>
                                    )}
                                </button>

                                {/* Reset Button */}
                                {(sourceFranchisee || targetFranchisee || previewData || transferResult) && (
                                    <button
                                        onClick={resetTransfer}
                                        disabled={isTransferring || isPreviewing}
                                        className="inline-flex items-center justify-center px-6 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Reset and start over"
                                    >
                                        <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Reset
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            {/* Preview Results */}
            {isPreviewing && (
                <div className="bg-white shadow-sm border border-gray-200 rounded-lg mb-6 p-8 flex items-center justify-center gap-3 text-gray-500 text-sm">
                    <svg className="animate-spin h-5 w-5 text-jims-cyan" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading households...
                </div>
            )}

            {/* Preview Results */}
            {previewData && (
                <div className="bg-white shadow-sm border border-gray-200 rounded-lg mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="ml-3">
                                    <h3 className="text-lg font-semibold text-gray-900">Select Households to Transfer</h3>
                                    <p className="text-sm text-gray-600">Review the details and select which households to transfer</p>
                                </div>
                            </div>
                            <button
                                onClick={clearPreview}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                title="Close preview"
                            >
                                <FaTimes className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    <div className="p-6">
                        {/* Transfer Summary */}
                        <div className="space-y-6">
                            {/* Transfer Behavior Info */}
                            {previewData.transferBehavior && (
                                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                                    <h4 className="text-sm font-semibold text-blue-900 mb-2">How This Transfer Works:</h4>
                                    <p className="text-sm text-blue-800">{previewData.transferBehavior.description}</p>
                                </div>
                            )}

                            {/* Transfer Overview */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-gray-900">
                                        {selectedHouseholdIds.size}
                                        <span className="text-lg font-normal text-gray-400"> / {previewData.affectedHouseholdsCount}</span>
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">Households Selected</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-green-600">{selectedSchedulesCount}</div>
                                    <div className="text-sm text-gray-600 mt-1">Schedules to Clone</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-blue-600">{selectedSchedulesCount}</div>
                                    <div className="text-sm text-gray-600 mt-1">Originals Marked Transferred</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-purple-600">{selectedPendingJobsCount}</div>
                                    <div className="text-sm text-gray-600 mt-1">Pending Jobs to Update</div>
                                </div>
                            </div>

                            {/* Transfer Direction */}
                            <div className="border-t border-gray-200 py-6">
                                <div className="text-center">
                                    <div className="text-sm text-gray-500 mb-3">Transfer Direction</div>
                                    <div className="flex items-center justify-center space-x-4">
                                        <div className="text-sm font-medium text-gray-900">
                                            {previewData.sourceFranchisee.name}
                                            <div className="text-xs text-gray-500">{previewData.sourceFranchisee.code}</div>
                                            <div className="text-xs text-blue-600 mt-1">Schedules marked "transferred"</div>
                                        </div>
                                        <FaArrowRight className="h-4 w-4 text-gray-400" />
                                        <div className="text-sm font-medium text-gray-900">
                                            {previewData.targetFranchisee.name}
                                            <div className="text-xs text-gray-500">{previewData.targetFranchisee.code}</div>
                                            <div className="text-xs text-green-600 mt-1">Receives cloned schedules</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Household Details */}
                        {previewData.householdDetails && previewData.householdDetails.length > 0 && (
                            <div className="border-t border-gray-200 pt-6">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-gray-900">
                                        Select Households to Transfer
                                        <span className="ml-2 text-gray-400 font-normal">({selectedHouseholdIds.size} of {previewData.householdDetails.length} selected)</span>
                                    </h4>
                                </div>
                                <div className="overflow-hidden rounded-lg border border-gray-200">
                                    <div className="max-h-96 overflow-y-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50 sticky top-0 z-10">
                                                <tr>
                                                    <th className="px-4 py-3 text-left w-10">
                                                        <input
                                                            type="checkbox"
                                                            checked={isAllSelected}
                                                            ref={el => { if (el) el.indeterminate = isSomeSelected; }}
                                                            onChange={handleSelectAll}
                                                            className="h-4 w-4 rounded border-gray-300 text-jims-cyan focus:ring-jims-cyan cursor-pointer"
                                                            title="Select all"
                                                        />
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Schedules</th>
                                                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Jobs</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {previewData.householdDetails.map((household, index) => {
                                                    const isSelected = selectedHouseholdIds.has(household.id);
                                                    return (
                                                        <tr
                                                            key={household.id}
                                                            onClick={() => handleToggleHousehold(household.id)}
                                                            className={`cursor-pointer transition-colors ${
                                                                isSelected
                                                                    ? 'bg-blue-50 hover:bg-blue-100'
                                                                    : index % 2 === 0
                                                                        ? 'bg-white hover:bg-gray-50'
                                                                        : 'bg-gray-50 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            <td className="px-4 py-3">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => handleToggleHousehold(household.id)}
                                                                    onClick={e => e.stopPropagation()}
                                                                    className="h-4 w-4 rounded border-gray-300 text-jims-cyan focus:ring-jims-cyan cursor-pointer"
                                                                />
                                                            </td>
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{household.name}</td>
                                                            <td className="px-4 py-3 text-sm text-gray-600">{household.address}</td>
                                                            <td className="px-4 py-3 text-sm text-gray-900 text-center">{household.scheduleCount}</td>
                                                            <td className="px-4 py-3 text-sm text-gray-900 text-center">{household.pendingJobCount}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                {selectedHouseholdIds.size === 0 && (
                                    <p className="text-xs text-amber-600 mt-2">Select at least one household to enable the transfer.</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmTransfer}
                title="Confirm Mass Client Transfer"
                message={
                    <div>
                        <div className="flex items-center mb-4 text-amber-600">
                            <FaExclamationTriangle className="mr-2" />
                            <span className="font-medium">Warning: This action cannot be undone!</span>
                        </div>
                        <p className="mb-4">
                            You are about to transfer <strong>{selectedHouseholdIds.size} selected household{selectedHouseholdIds.size !== 1 ? 's' : ''} and their pending jobs</strong> from:
                        </p>
                        <div className="bg-gray-50 p-4 rounded-md mb-4">
                            <div className="mb-2">
                                <strong>FROM:</strong> {sourceFranchisee?.franchiseName} ({sourceFranchisee?.fseCode})
                            </div>
                            <div>
                                <strong>TO:</strong> {targetFranchisee?.franchiseName} ({targetFranchisee?.fseCode})
                            </div>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                            <p className="text-sm text-blue-800">
                                <strong>What will happen:</strong>
                            </p>
                            <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
                                <li>Job schedules will be <strong>cloned</strong> to {targetFranchisee?.franchiseName}</li>
                                <li>Original schedules will be marked as "transferred" for {sourceFranchisee?.franchiseName}</li>
                                <li>All pending jobs will be updated to reference the new cloned schedules</li>
                                <li>Future jobs will be generated for {targetFranchisee?.franchiseName}</li>
                            </ul>
                        </div>
                        <p className="text-sm text-gray-600">
                            This will clone all active job schedules and update all pending jobs (pending-quote, quote-received, pending-approval).
                        </p>
                    </div>
                }
                isLoading={isTransferring}
                loadingText="Transferring clients..."
            />
        </>
    );
};

export default MassClientTransferUtility;
